import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useScrollRestore } from '../hooks/useScrollRestore';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Users, Globe } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const todayStr = () => new Date().toISOString().split('T')[0];
const shiftDate = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0]; };

const FILTERS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7days', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'custom', label: 'Custom' },
];

/**
 * InquiryPanel — shared inquiry leads view for Admin + Handler, web + mobile.
 * Filter: Today / Yesterday / Weekly / Monthly / Custom (single date OR from→to
 * range). Clicking a lead calls onOpenChat(chatRoomId) so the parent decides
 * split (desktop) vs navigate (mobile) — never a forced full-screen.
 */
const InquiryPanel = ({ user, onOpenChat }) => {
  const [filter, setFilter] = useState('today');
  const [customMode, setCustomMode] = useState('single'); // 'single' | 'range'
  const [singleDate, setSingleDate] = useState(todayStr());
  const [fromDate, setFromDate] = useState(shiftDate(-30));
  const [toDate, setToDate] = useState(todayStr());
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const listScrollRef = useScrollRestore('inquiry_panel_scroll', [list.length]);

  const params = useMemo(() => {
    if (filter === 'today') return { date: todayStr() };
    if (filter === 'yesterday') return { date: shiftDate(-1) };
    if (filter === '7days') return { days: 7 };
    if (filter === 'monthly') return { days: 30 };
    if (filter === 'custom') {
      return customMode === 'single' ? { date: singleDate } : { from_date: fromDate, to_date: toDate };
    }
    return {};
  }, [filter, customMode, singleDate, fromDate, toDate]);

  const load = useCallback(async () => {
    try {
      const l = await axios.get(`${API}/inquiry/list`, { params: { ...params, user_id: user?.id } });
      setList(l.data.inquiries || []);
      setLoading(false);
    } catch (_) { setLoading(false); }
  }, [params, user?.id]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => { const t = setInterval(() => { if (document.visibilityState === 'hidden') return; load(); }, 15000); const onVis = () => { if (document.visibilityState === 'visible') load(); }; document.addEventListener('visibilitychange', onVis); return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); }; }, [load]);

  const headingText = () => {
    if (filter === 'today') return "Today's Inquiries";
    if (filter === 'yesterday') return "Yesterday's Inquiries";
    if (filter === '7days') return 'Last 7 Days Inquiries';
    if (filter === 'monthly') return 'Last 30 Days Inquiries';
    if (filter === 'custom' && customMode === 'single') return `Inquiries on ${singleDate}`;
    return `Inquiries ${fromDate} → ${toDate}`;
  };

  const inquiryLink = `${window.location.origin}/inquiry-chat`;

  return (
    <div className="p-3 space-y-3" data-testid="inquiry-panel">
      {/* Shareable link (compact) */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-2.5">
        <div className="flex gap-2 items-center">
          <input type="text" value={inquiryLink} readOnly className="flex-1 text-xs bg-white border rounded px-2 py-1" data-testid="inq-link-input" />
          <button onClick={() => { navigator.clipboard.writeText(inquiryLink); toast.success('Link copied!'); }}
            className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700 flex items-center gap-1 flex-shrink-0" data-testid="inq-copy-link">
            <Copy size={13} /> Copy
          </button>
        </div>
      </div>

      {/* P10 — filter chips as a single scrollable row (no "Filter" label, no analytics boxes) */}
      <div className="bg-white rounded-lg border p-2">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${filter === f.key ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid={`inq-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {filter === 'custom' && (
          <div className="mt-3 border-t pt-3 space-y-3" data-testid="inq-custom">
            <div className="flex gap-2">
              <button onClick={() => setCustomMode('single')} className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold ${customMode === 'single' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="inq-custom-single">
                Single Date
              </button>
              <button onClick={() => setCustomMode('range')} className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold ${customMode === 'range' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="inq-custom-range">
                From → To
              </button>
            </div>
            {customMode === 'single' ? (
              <input type="date" value={singleDate} max={todayStr()} onChange={(e) => setSingleDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:border-orange-400" data-testid="inq-single-date" style={{ colorScheme: 'light' }} />
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <label className="block text-[11px] text-gray-500 mb-1">From</label>
                  <input type="date" value={fromDate} max={todayStr()} onChange={(e) => setFromDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm bg-gray-50 focus:bg-white" data-testid="inq-from-date" style={{ colorScheme: 'light' }} />
                </div>
                <div className="flex-1">
                  <label className="block text-[11px] text-gray-500 mb-1">To</label>
                  <input type="date" value={toDate} max={todayStr()} min={fromDate} onChange={(e) => setToDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm bg-gray-50 focus:bg-white" data-testid="inq-to-date" style={{ colorScheme: 'light' }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* List */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="p-3 border-b bg-gradient-to-r from-orange-50 to-purple-50">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Users size={16} weight="bold" /> {headingText()}
            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full" data-testid="inq-list-count">{list.length}</span>
          </h3>
        </div>
        {loading ? (
          <p className="p-8 text-center text-gray-400 text-sm">Loading…</p>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-gray-400"><div className="text-4xl mb-2">📭</div><p>No inquiries found</p></div>
        ) : (
          <div ref={listScrollRef} className="divide-y max-h-[62vh] overflow-y-auto pb-20">
            {list.map((inq) => (
              <div
                key={inq.inquiry_id || inq.chat_room_id}
                className="p-3 hover:bg-orange-50 cursor-pointer transition-colors active:bg-orange-100"
                onClick={() => inq.chat_room_id && onOpenChat && onOpenChat(inq.chat_room_id)}
                data-testid={`inq-item-${inq.inquiry_id || inq.chat_room_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-bold shadow-sm flex-shrink-0">
                      {(inq.guest_name || 'C').replace('Client ', '').slice(0, 3)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 truncate">{inq.guest_name || 'Guest'}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 truncate"><Globe size={11} /> {inq.ip_address || '—'}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-500">{inq.timestamp ? new Date(inq.timestamp).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : ''}</p>
                    {(inq.messages_count > 0) && <p className="text-xs text-purple-600 font-medium">{inq.messages_count} msg</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InquiryPanel;
